/**
 * Strategic Initiatives Data Management (Front-End Only)
 * LocalStorage persistence. Used by Strategic Initiatives React components.
 */

export const InitiativesDB = {
  STORAGE_KEYS: {
    INITIATIVES: 'nexonurse_initiatives',
    KPIS: 'nexonurse_initiative_kpis',
    TAGS: 'nexonurse_initiative_tags',
    TAG_ASSIGNMENTS: 'nexonurse_initiative_tag_assignments',
    TEAM_MEMBERS: 'nexonurse_initiative_team_members',
    MILESTONES: 'nexonurse_initiative_milestones',
    STATUS_UPDATES: 'nexonurse_initiative_status_updates',
    FEATURE_TOGGLE: 'nexonurse_feature_strategic_initiatives',
    ADD_TO_QUEUE: 'nexonurse_add_to_queue_pending'
  },

  init() {
    if (localStorage.getItem(this.STORAGE_KEYS.FEATURE_TOGGLE) == null) {
      this.setFeatureToggle({
        enabled: false,
        enabledForRoles: ['super-user'],
        enabledForUsers: []
      });
    }
    if (localStorage.getItem(this.STORAGE_KEYS.INITIATIVES) == null) {
      localStorage.setItem(this.STORAGE_KEYS.INITIATIVES, JSON.stringify([]));
    }
    if (localStorage.getItem(this.STORAGE_KEYS.TAGS) == null) {
      this.seedDefaultTags();
    }
  },

  getFeatureToggle() {
    const data = localStorage.getItem(this.STORAGE_KEYS.FEATURE_TOGGLE);
    return data ? JSON.parse(data) : null;
  },

  setFeatureToggle(toggle) {
    localStorage.setItem(this.STORAGE_KEYS.FEATURE_TOGGLE, JSON.stringify(toggle));
  },

  isFeatureEnabled(userRole) {
    const toggle = this.getFeatureToggle();
    if (!toggle) return false;
    if (toggle.enabled) return true;
    return (toggle.enabledForRoles || []).includes(userRole);
  },

  getAllInitiatives() {
    const data = localStorage.getItem(this.STORAGE_KEYS.INITIATIVES);
    return data ? JSON.parse(data) : [];
  },

  getInitiative(id) {
    const initiatives = this.getAllInitiatives();
    const initiative = initiatives.find(i => i.id === id);
    if (!initiative) return null;
    initiative.kpis = this.getKPIsForInitiative(id);
    initiative.tags = this.getTagsForInitiative(id);
    initiative.team = this.getTeamMembersForInitiative(id);
    initiative.milestones = this.getMilestonesForInitiative(id);
    initiative.statusUpdates = this.getStatusUpdatesForInitiative(id);
    if (initiative.overallHealth == null) {
      const kpiHealth = (initiative.kpis || []).length
        ? (initiative.kpis.some(k => k.currentHealth === 'red') ? 'red' : initiative.kpis.some(k => k.currentHealth === 'yellow') ? 'yellow' : 'green')
        : 'green';
      const timelineHealth = this.calculateTimelineHealth(initiative.dueDate, initiative.percentComplete);
      initiative.overallHealth = (kpiHealth === 'red' || timelineHealth === 'red') ? 'red' : (kpiHealth === 'yellow' || timelineHealth === 'yellow') ? 'yellow' : 'green';
    }
    return initiative;
  },

  /** Alias for getInitiative (enriched with KPIs, tags, milestones, statusUpdates, team). */
  getInitiativeEnriched(id) {
    return this.getInitiative(id);
  },

  saveInitiative(initiative) {
    const initiatives = this.getAllInitiatives();
    const existingIndex = initiatives.findIndex(i => i.id === initiative.id);
    initiative.updatedAt = new Date().toISOString();
    if (existingIndex >= 0) {
      initiatives[existingIndex] = initiative;
    } else {
      initiative.createdAt = new Date().toISOString();
      initiatives.push(initiative);
    }
    localStorage.setItem(this.STORAGE_KEYS.INITIATIVES, JSON.stringify(initiatives));
    return initiative;
  },

  deleteInitiative(id) {
    const initiatives = this.getAllInitiatives().filter(i => i.id !== id);
    localStorage.setItem(this.STORAGE_KEYS.INITIATIVES, JSON.stringify(initiatives));
    this.deleteKPIsForInitiative(id);
    this.deleteTagAssignmentsForInitiative(id);
    this.deleteTeamMembersForInitiative(id);
    this.deleteMilestonesForInitiative(id);
    this.deleteStatusUpdatesForInitiative(id);
  },

  getAllKPIs() {
    const data = localStorage.getItem(this.STORAGE_KEYS.KPIS);
    return data ? JSON.parse(data) : [];
  },

  getKPIsForInitiative(initiativeId) {
    return this.getAllKPIs().filter(k => k.initiativeId === initiativeId);
  },

  saveKPI(kpi) {
    const kpis = this.getAllKPIs();
    kpi.currentHealth = this.calculateKPIHealth(kpi.currentValue, kpi.targetValue, kpi.targetOperator);
    kpi.lastUpdated = new Date().toISOString();
    const idx = kpis.findIndex(k => k.id === kpi.id);
    if (idx >= 0) kpis[idx] = kpi;
    else kpis.push(kpi);
    localStorage.setItem(this.STORAGE_KEYS.KPIS, JSON.stringify(kpis));
    this.updateInitiativeHealth(kpi.initiativeId);
    return kpi;
  },

  deleteKPI(id) {
    const kpis = this.getAllKPIs().filter(k => k.id !== id);
    const kpi = this.getAllKPIs().find(k => k.id === id);
    localStorage.setItem(this.STORAGE_KEYS.KPIS, JSON.stringify(kpis));
    if (kpi) this.updateInitiativeHealth(kpi.initiativeId);
  },

  deleteKPIsForInitiative(initiativeId) {
    const kpis = this.getAllKPIs().filter(k => k.initiativeId !== initiativeId);
    localStorage.setItem(this.STORAGE_KEYS.KPIS, JSON.stringify(kpis));
  },

  calculateKPIHealth(currentValue, targetValue, operator) {
    if (currentValue == null || targetValue == null) return 'gray';
    let meets = false;
    switch (operator) {
      case '<': meets = currentValue < targetValue; break;
      case '<=': meets = currentValue <= targetValue; break;
      case '>': meets = currentValue > targetValue; break;
      case '>=': meets = currentValue >= targetValue; break;
      case '==': meets = currentValue === targetValue; break;
      default: return 'gray';
    }
    return meets ? 'green' : 'red';
  },

  getAllTags() {
    const data = localStorage.getItem(this.STORAGE_KEYS.TAGS);
    return data ? JSON.parse(data) : [];
  },

  saveTag(tag) {
    const tags = this.getAllTags();
    const idx = tags.findIndex(t => t.id === tag.id);
    if (idx >= 0) tags[idx] = tag;
    else tags.push(tag);
    localStorage.setItem(this.STORAGE_KEYS.TAGS, JSON.stringify(tags));
    return tag;
  },

  seedDefaultTags() {
    const defaultTags = [
      { id: 'tag-1', label: 'high-priority', color: '#EF4444', category: 'priority' },
      { id: 'tag-2', label: 'medium-priority', color: '#F59E0B', category: 'priority' },
      { id: 'tag-3', label: 'low-priority', color: '#10B981', category: 'priority' },
      { id: 'tag-4', label: 'access', color: '#2C5FA8', category: 'pillar' },
      { id: 'tag-5', label: 'quality', color: '#00CED1', category: 'pillar' },
      { id: 'tag-6', label: 'staffing', color: '#5E60CE', category: 'pillar' },
      { id: 'tag-7', label: 'finance', color: '#10B981', category: 'pillar' },
      { id: 'tag-8', label: 'Q1-2026', color: '#6B7280', category: 'quarter' },
      { id: 'tag-9', label: 'Q2-2026', color: '#6B7280', category: 'quarter' },
      { id: 'tag-10', label: 'regulatory', color: '#EF4444', category: 'compliance' }
    ];
    localStorage.setItem(this.STORAGE_KEYS.TAGS, JSON.stringify(defaultTags));
  },

  getAllTagAssignments() {
    const data = localStorage.getItem(this.STORAGE_KEYS.TAG_ASSIGNMENTS);
    return data ? JSON.parse(data) : [];
  },

  getTagsForInitiative(initiativeId) {
    const assignments = this.getAllTagAssignments().filter(a => a.initiativeId === initiativeId);
    const tagIds = assignments.map(a => a.tagId);
    return this.getAllTags().filter(t => tagIds.includes(t.id));
  },

  assignTag(initiativeId, tagId) {
    const assignments = this.getAllTagAssignments();
    if (assignments.some(a => a.initiativeId === initiativeId && a.tagId === tagId)) return;
    assignments.push({ initiativeId, tagId, assignedAt: new Date().toISOString() });
    localStorage.setItem(this.STORAGE_KEYS.TAG_ASSIGNMENTS, JSON.stringify(assignments));
  },

  removeTag(initiativeId, tagId) {
    const assignments = this.getAllTagAssignments().filter(
      a => !(a.initiativeId === initiativeId && a.tagId === tagId)
    );
    localStorage.setItem(this.STORAGE_KEYS.TAG_ASSIGNMENTS, JSON.stringify(assignments));
  },

  deleteTagAssignmentsForInitiative(initiativeId) {
    const assignments = this.getAllTagAssignments().filter(a => a.initiativeId !== initiativeId);
    localStorage.setItem(this.STORAGE_KEYS.TAG_ASSIGNMENTS, JSON.stringify(assignments));
  },

  getAllTeamMembers() {
    const data = localStorage.getItem(this.STORAGE_KEYS.TEAM_MEMBERS);
    return data ? JSON.parse(data) : [];
  },

  getTeamMembersForInitiative(initiativeId) {
    return this.getAllTeamMembers().filter(m => m.initiativeId === initiativeId);
  },

  saveTeamMember(member) {
    const members = this.getAllTeamMembers();
    const idx = members.findIndex(m => m.id === member.id);
    if (idx >= 0) members[idx] = member;
    else members.push(member);
    localStorage.setItem(this.STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(members));
    return member;
  },

  deleteTeamMember(id) {
    const members = this.getAllTeamMembers().filter(m => m.id !== id);
    localStorage.setItem(this.STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(members));
  },

  deleteTeamMembersForInitiative(initiativeId) {
    const members = this.getAllTeamMembers().filter(m => m.initiativeId !== initiativeId);
    localStorage.setItem(this.STORAGE_KEYS.TEAM_MEMBERS, JSON.stringify(members));
  },

  getAllMilestones() {
    const data = localStorage.getItem(this.STORAGE_KEYS.MILESTONES);
    return data ? JSON.parse(data) : [];
  },

  getMilestonesForInitiative(initiativeId) {
    return this.getAllMilestones()
      .filter(m => m.initiativeId === initiativeId)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },

  saveMilestone(milestone) {
    const milestones = this.getAllMilestones();
    const idx = milestones.findIndex(m => m.id === milestone.id);
    if (idx >= 0) milestones[idx] = milestone;
    else milestones.push(milestone);
    localStorage.setItem(this.STORAGE_KEYS.MILESTONES, JSON.stringify(milestones));
    return milestone;
  },

  deleteMilestone(id) {
    const milestones = this.getAllMilestones().filter(m => m.id !== id);
    localStorage.setItem(this.STORAGE_KEYS.MILESTONES, JSON.stringify(milestones));
  },

  deleteMilestonesForInitiative(initiativeId) {
    const milestones = this.getAllMilestones().filter(m => m.initiativeId !== initiativeId);
    localStorage.setItem(this.STORAGE_KEYS.MILESTONES, JSON.stringify(milestones));
  },

  getAllStatusUpdates() {
    const data = localStorage.getItem(this.STORAGE_KEYS.STATUS_UPDATES);
    return data ? JSON.parse(data) : [];
  },

  getStatusUpdatesForInitiative(initiativeId) {
    return this.getAllStatusUpdates()
      .filter(u => u.initiativeId === initiativeId)
      .sort((a, b) => new Date(b.updateDate) - new Date(a.updateDate));
  },

  saveStatusUpdate(update) {
    const updates = this.getAllStatusUpdates();
    if (!update.kpiSnapshot) {
      const kpis = this.getKPIsForInitiative(update.initiativeId);
      update.kpiSnapshot = {};
      kpis.forEach(kpi => { update.kpiSnapshot[kpi.kpiName] = `${kpi.currentValue} ${kpi.unit}`; });
    }
    const idx = updates.findIndex(u => u.id === update.id);
    if (idx >= 0) updates[idx] = update;
    else { update.createdAt = new Date().toISOString(); updates.push(update); }
    localStorage.setItem(this.STORAGE_KEYS.STATUS_UPDATES, JSON.stringify(updates));
    if (update.percentComplete !== undefined || update.status) {
      const initiative = this.getInitiative(update.initiativeId);
      if (initiative) {
        if (update.percentComplete !== undefined) initiative.percentComplete = update.percentComplete;
        if (update.status) initiative.status = update.status;
        this.saveInitiative(initiative);
      }
    }
    return update;
  },

  deleteStatusUpdate(id) {
    const updates = this.getAllStatusUpdates().filter(u => u.id !== id);
    localStorage.setItem(this.STORAGE_KEYS.STATUS_UPDATES, JSON.stringify(updates));
  },

  deleteStatusUpdatesForInitiative(initiativeId) {
    const updates = this.getAllStatusUpdates().filter(u => u.initiativeId !== initiativeId);
    localStorage.setItem(this.STORAGE_KEYS.STATUS_UPDATES, JSON.stringify(updates));
  },

  updateInitiativeHealth(initiativeId) {
    const initiative = this.getInitiative(initiativeId);
    if (!initiative) return;
    const kpis = this.getKPIsForInitiative(initiativeId);
    let kpiHealth = 'green';
    const healths = kpis.map(k => k.currentHealth);
    if (healths.includes('red')) kpiHealth = 'red';
    else if (healths.includes('yellow')) kpiHealth = 'yellow';
    const timelineHealth = this.calculateTimelineHealth(initiative.dueDate, initiative.percentComplete);
    let overallHealth = 'green';
    if (kpiHealth === 'red' || timelineHealth === 'red') overallHealth = 'red';
    else if (kpiHealth === 'yellow' || timelineHealth === 'yellow') overallHealth = 'yellow';
    initiative.kpiHealth = kpiHealth;
    initiative.timelineHealth = timelineHealth;
    initiative.overallHealth = overallHealth;
    this.saveInitiative(initiative);
  },

  calculateTimelineHealth(dueDate, percentComplete) {
    if (!dueDate) return 'green';
    const now = new Date();
    const due = new Date(dueDate);
    const daysRemaining = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    if (daysRemaining < 0) return 'red';
    if (daysRemaining < 30 && (percentComplete || 0) < 75) return 'yellow';
    if (daysRemaining < 60 && (percentComplete || 0) < 50) return 'yellow';
    return 'green';
  },

  generateId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  },

  seedDemoData() {
    const demoInitiative = {
      id: this.generateId('INIT'),
      name: 'Reduce ED Wait Times',
      description: 'Decrease average ED wait time to under 45 minutes through improved triage and fast-track processes',
      category: 'access',
      status: 'in-progress',
      percentComplete: 65,
      startDate: '2026-01-15',
      dueDate: '2026-06-30',
      programManagerId: 'user-123',
      programManagerName: 'Sarah Chen',
      overallHealth: 'green',
      kpiHealth: 'yellow',
      timelineHealth: 'green',
      createdBy: 'user-123',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    this.saveInitiative(demoInitiative);
    this.saveKPI({
      id: this.generateId('KPI'),
      initiativeId: demoInitiative.id,
      kpiName: 'Average ED Wait Time',
      targetValue: 45,
      targetOperator: '<',
      unit: 'minutes',
      currentValue: 52,
      currentHealth: 'red'
    });
    this.saveKPI({
      id: this.generateId('KPI'),
      initiativeId: demoInitiative.id,
      kpiName: 'LWBS Rate',
      targetValue: 2,
      targetOperator: '<',
      unit: '%',
      currentValue: 3.1,
      currentHealth: 'red'
    });
    this.assignTag(demoInitiative.id, 'tag-1');
    this.assignTag(demoInitiative.id, 'tag-4');
    this.assignTag(demoInitiative.id, 'tag-9');
    this.saveMilestone({
      id: this.generateId('MILE'),
      initiativeId: demoInitiative.id,
      name: 'New triage process implemented',
      dueDate: '2026-03-15',
      completed: false
    });
    this.saveStatusUpdate({
      id: this.generateId('UPDATE'),
      initiativeId: demoInitiative.id,
      authorId: 'user-123',
      authorName: 'Sarah Chen',
      updateDate: new Date().toISOString().split('T')[0],
      status: 'on-track',
      summary: 'New fast-track process showing 15% reduction in wait times',
      details: 'Implemented express care for low-acuity patients. Early results are promising.',
      percentComplete: 65,
      accomplishments: ['Reduced average wait time by 15%', 'Staff training completed for all ED nurses'],
      blockers: [],
      nextSteps: ['Roll out to night shift next week', 'Schedule follow-up metrics review']
    });
  },

  // ========== ADD TO QUEUE (Priority Queue integration) ==========
  getAddToQueuePending() {
    const data = localStorage.getItem(this.STORAGE_KEYS.ADD_TO_QUEUE);
    return data ? JSON.parse(data) : [];
  },

  addInitiativeToQueue(initiative) {
    const list = this.getAddToQueuePending();
    if (list.some(item => item.initiativeId === initiative.id)) return;
    list.push({
      initiativeId: initiative.id,
      initiativeName: initiative.name,
      addedAt: new Date().toISOString(),
      type: 'initiative'
    });
    localStorage.setItem(this.STORAGE_KEYS.ADD_TO_QUEUE, JSON.stringify(list));
  },

  clearAddToQueuePending() {
    localStorage.removeItem(this.STORAGE_KEYS.ADD_TO_QUEUE);
  },

  /** Returns tasks for Priority Queue: incomplete milestones + "submit update" if no update in 30 days. */
  getInitiativeQueueItems() {
    const initiatives = this.getAllInitiatives();
    const tasks = [];
    const now = new Date();
    const daysSince = (dateStr) => {
      if (!dateStr) return 999;
      const d = new Date(dateStr);
      return Math.floor((now - d) / (1000 * 60 * 60 * 24));
    };
    const getNextMonday = () => {
      const d = new Date(now);
      let day = d.getDay();
      const add = day === 0 ? 1 : day === 1 ? 7 : 8 - day;
      d.setDate(d.getDate() + add);
      return d.toISOString().split('T')[0];
    };

    initiatives.forEach(initiative => {
      const milestones = this.getMilestonesForInitiative(initiative.id);
      milestones.forEach(m => {
        if (!m.completed) {
          tasks.push({
            id: `initiative-${m.id}`,
            title: `[Initiative] ${m.name}`,
            type: 'initiative-milestone',
            dueDate: m.dueDate,
            scheduledDate: m.dueDate,
            initiativeId: initiative.id,
            initiativeName: initiative.name,
            priority: initiative.overallHealth === 'red' ? 'high' : 'medium',
            status: 'backlog',
            category: 'Initiatives',
            notes: initiative.name
          });
        }
      });
      const updates = this.getStatusUpdatesForInitiative(initiative.id);
      if (updates.length === 0 || daysSince(updates[0].updateDate) > 30) {
        const nextMon = getNextMonday();
        tasks.push({
          id: `initiative-update-${initiative.id}`,
          title: `[Required] Submit status update: ${initiative.name}`,
          type: 'initiative-update-required',
          dueDate: nextMon,
          scheduledDate: nextMon,
          initiativeId: initiative.id,
          initiativeName: initiative.name,
          priority: 'high',
          status: 'backlog',
          category: 'Initiatives',
          notes: 'No update in 30+ days'
        });
      }
    });
    return tasks;
  },

  /** Adds one more demo initiative with full data (KPIs, tags, milestones, status update). */
  seedEnhancedDemoData() {
    const initId = this.generateId('INIT');
    const initiative = {
      id: initId,
      name: 'Reduce ED Wait Times',
      description: 'Decrease average ED wait time to under 45 minutes through improved triage and fast-track processes',
      category: 'access',
      status: 'in-progress',
      percentComplete: 65,
      startDate: '2026-01-15',
      dueDate: '2026-06-30',
      programManagerId: 'user-123',
      programManagerName: 'Sarah Chen',
      overallHealth: 'yellow',
      kpiHealth: 'yellow',
      timelineHealth: 'green',
      createdBy: 'user-123',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    this.saveInitiative(initiative);
    this.saveKPI({
      id: this.generateId('KPI'),
      initiativeId: initId,
      kpiName: 'Average ED Wait Time',
      targetValue: 45,
      targetOperator: '<',
      unit: 'minutes',
      currentValue: 52,
      currentHealth: 'red'
    });
    this.saveKPI({
      id: this.generateId('KPI'),
      initiativeId: initId,
      kpiName: 'LWBS Rate',
      targetValue: 2,
      targetOperator: '<',
      unit: '%',
      currentValue: 3.1,
      currentHealth: 'yellow'
    });
    this.assignTag(initId, 'tag-1');
    this.assignTag(initId, 'tag-4');
    this.assignTag(initId, 'tag-9');
    this.saveMilestone({
      id: this.generateId('MILE'),
      initiativeId: initId,
      name: 'New triage process implemented',
      dueDate: '2026-03-15',
      completed: false
    });
    this.saveMilestone({
      id: this.generateId('MILE'),
      initiativeId: initId,
      name: 'Achieve target consistently',
      dueDate: '2026-06-30',
      completed: false
    });
    this.saveStatusUpdate({
      id: this.generateId('UPDATE'),
      initiativeId: initId,
      authorId: 'user-123',
      authorName: 'Sarah Chen',
      updateDate: new Date().toISOString().split('T')[0],
      status: 'on-track',
      summary: 'New fast-track process showing 15% reduction in wait times',
      details: 'Implemented express care for low-acuity patients. Early results promising.',
      percentComplete: 65
    });
  }
};

InitiativesDB.init();
